import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';

export function LoginPage() {
  const { user, loading, signInWithGoogle } = useAuth();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (user) {
    const next = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={next} replace />;
  }

  const handleGoogle = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const next = (location.state as { from?: string } | null)?.from ?? '/';
      await signInWithGoogle(window.location.origin + next);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-accent/10 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-elevated">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-foreground text-2xs text-background">
            ✦
          </span>
          Flax Marketing Dashboard
        </div>
        <h1 className="mt-6 text-2xl font-semibold tracking-tight">Sign in to continue</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Authorized Flax team only. Use your <span className="font-medium">@flaxitup.com</span> /{' '}
          <span className="font-medium">@flaxfoods.in</span> Google account.
        </p>

        <Button
          type="button"
          onClick={handleGoogle}
          disabled={submitting}
          className="mt-8 w-full justify-center gap-2"
          size="lg"
        >
          <GoogleIcon />
          {submitting ? 'Redirecting to Google…' : 'Continue with Google'}
        </Button>

        {error && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-600 dark:text-rose-400">
            {error}
          </div>
        )}

        <p className="mt-6 text-2xs text-muted-foreground">
          By signing in you agree to handle Flax data confidentially. Access is logged and revocable.
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path
        fill="#EA4335"
        d="M12 11v3.2h4.6c-.2 1.2-1.5 3.6-4.6 3.6-2.8 0-5-2.3-5-5.1S9.2 7.6 12 7.6c1.6 0 2.6.7 3.2 1.3l2.2-2.1C16 5.5 14.2 4.6 12 4.6c-4 0-7.2 3.3-7.2 7.4S8 19.4 12 19.4c4.2 0 7-2.9 7-7 0-.5 0-.8-.1-1.2H12z"
      />
    </svg>
  );
}
